import React, { useContext, useState } from 'react';
import {
  MDBContainer,
  MDBRow,
  MDBCol,
  MDBTypography,
  MDBBtn,
  MDBAlert,
  MDBCard,
  MDBCardBody,
  MDBIcon,
  MDBBadge,
  MDBInput,
  MDBTextArea
} from 'mdb-react-ui-kit';
import { useTranslation } from 'react-i18next';
import ReactCountryFlag from 'react-country-flag';
import { useFormData } from '../hooks/useFormData';
import { useStepNavigation } from '../hooks/useStepNavigation';
import { useValidation } from '../components/ValidationProvider';
import { getFieldValidationRules } from '../validation/lectures';
import { DevContext } from '../../../../../App';
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';

// Default subject structure with initial empty subject (ID only for drag&drop, not saved)
const createEmptySubject = () => ({
  id: `subject-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
  de: '',
  en: ''
});

// Settings for subject constraints (later configurable from lecture settings)
const SUBJECTS_SETTINGS = {
  minSubjects: 3,
  maxSubjects: 5
};

export default function SubjectsStep({ onComplete }) {
  const { t } = useTranslation();
  const { formData, handleFieldChange, hasFieldError, getFieldError } = useFormData();
  const { goToNextStep, goToPreviousStep } = useStepNavigation();
  const { getStepValidation, getStepRequiredFields, getFieldValidation, eventConfig } = useValidation();
  const { isDevMode } = useContext(DevContext);

  // Get dynamic character limits from schema (same as title)
  const subjectValidationRules = getFieldValidationRules(eventConfig, 'title', formData);
  const MIN_CHARACTERS = subjectValidationRules['text.minLength'] || 10;
  const MAX_CHARACTERS = subjectValidationRules['text.maxLength'] || 60;

  // Initialize subjects from form data or create default structure with all 5 inputs
  const [subjects, setSubjects] = useState(() => {
    if (formData.subjects && formData.subjects.length > 0) {
      // Add IDs for drag&drop if they don't exist
      const existingSubjects = formData.subjects.map((subject, index) => ({
        id: subject.id || `subject-${Date.now()}-${index}`,
        de: subject.de || '',
        en: subject.en || ''
      }));
      // Fill up to 5 subjects if needed
      while (existingSubjects.length < SUBJECTS_SETTINGS.maxSubjects) {
        existingSubjects.push(createEmptySubject());
      }
      return existingSubjects;
    }
    // Create all 5 empty subjects by default
    return Array.from({ length: SUBJECTS_SETTINGS.maxSubjects }, () => createEmptySubject());
  });

  // Get validation info from schema
  const stepValidation = getStepValidation('subjects');
  const requiredFields = getStepRequiredFields('subjects');
  const subjectsFieldValidation = getFieldValidation('subjects');

  const handleSubjectsChange = (newSubjects) => {
    setSubjects(newSubjects);
    // Remove IDs before saving - only save the order and content
    const subjectsForSaving = newSubjects.map(({ de, en }) => ({ de, en }));
    handleFieldChange('subjects', subjectsForSaving);
  };

  // Character counting functions
  const getCharacterCount = (text) => {
    return text.length;
  };

  const getRemainingChars = (text) => {
    const count = getCharacterCount(text);
    return Math.max(0, MIN_CHARACTERS - count);
  };

  const getExcessChars = (text) => {
    const count = getCharacterCount(text);
    return Math.max(0, count - MAX_CHARACTERS);
  };

  const isTextValid = (text) => {
    const count = getCharacterCount(text);
    return count >= MIN_CHARACTERS && count <= MAX_CHARACTERS;
  };

  const addSubject = () => {
    if (subjects.length < SUBJECTS_SETTINGS.maxSubjects) {
      const newSubjects = [...subjects, createEmptySubject()];
      handleSubjectsChange(newSubjects);
    }
  };

  const removeSubject = (index) => {
    if (subjects.length > SUBJECTS_SETTINGS.minSubjects) {
      const newSubjects = subjects.filter((_, i) => i !== index);
      handleSubjectsChange(newSubjects);
    }
  };

  const updateSubject = (index, language, value) => {
    const newSubjects = subjects.map((subject, i) => 
      i === index ? { ...subject, [language]: value } : subject
    );
    handleSubjectsChange(newSubjects);
  };

  const onDragEnd = (result) => {
    if (!result.destination) {
      return;
    }

    const items = Array.from(subjects);
    const [reorderedItem] = items.splice(result.source.index, 1);
    items.splice(result.destination.index, 0, reorderedItem);

    handleSubjectsChange(items);
  };

  const isValidSubjects = () => {
    if (subjects.length < SUBJECTS_SETTINGS.minSubjects || subjects.length > SUBJECTS_SETTINGS.maxSubjects) {
      return false;
    }
    
    // Count filled subjects (both de and en must be filled and valid length)
    const filledSubjects = subjects.filter(subject => 
      isTextValid(subject.de.trim()) && isTextValid(subject.en.trim())
    );
    
    // Check if we have at least the minimum required filled subjects
    if (filledSubjects.length < SUBJECTS_SETTINGS.minSubjects) {
      return false;
    }
    
    // Check for partially filled subjects (only one language filled)
    const hasPartiallyFilledSubjects = subjects.some(subject => {
      const hasGerman = subject.de.trim().length > 0;
      const hasEnglish = subject.en.trim().length > 0;
      return (hasGerman && !hasEnglish) || (!hasGerman && hasEnglish);
    });
    
    // Check for subjects with invalid character counts
    const hasInvalidLengthSubjects = subjects.some(subject => {
      const hasGerman = subject.de.trim().length > 0;
      const hasEnglish = subject.en.trim().length > 0;
      if (hasGerman && !isTextValid(subject.de.trim())) return true;
      if (hasEnglish && !isTextValid(subject.en.trim())) return true;
      return false;
    });
    
    return !hasPartiallyFilledSubjects && !hasInvalidLengthSubjects;
  };

  const handleContinue = () => {
    if (isValidSubjects() && stepValidation.isValid) {
      onComplete?.();
      goToNextStep();
    }
  };

  const canContinue = isValidSubjects() && stepValidation.isValid;

  return (
    <div className="d-flex flex-column h-100">
      <MDBContainer className="flex-grow-1 pb-5">
      <MDBTypography tag="h3" className="mb-4">
        {t('Lecture Subjects')}
      </MDBTypography>
      
      <MDBRow className="mb-4">
        <MDBCol>
          <MDBTypography tag="p" className="mb-4 text-muted">
            {t('Please define the main subjects/content areas of your lecture. Each subject should be between {{min}} and {{max}} characters long. You can reorder them by dragging and dropping.', { min: MIN_CHARACTERS, max: MAX_CHARACTERS })}
          </MDBTypography>
          
          <div className="d-flex justify-content-between align-items-center mb-3">
            <div>
              <MDBTypography tag="h6" className="mb-1">
                {/* {t('Lecture Subjects')} */}
                {/* ({subjects.length}/{SUBJECTS_SETTINGS.maxSubjects}) */}
              </MDBTypography>
              <small className="text-muted">
                {t('Minimum')}: {SUBJECTS_SETTINGS.minSubjects}, {t('Maximum')}: {SUBJECTS_SETTINGS.maxSubjects}
              </small>
            </div>
            {/* Temporarily commented out Add Subject button
            <MDBBtn
              size="sm"
              onClick={addSubject}
              disabled={subjects.length >= SUBJECTS_SETTINGS.maxSubjects}
            >
              <MDBIcon icon="plus" className="me-1" />
              {t('Add Subject')}
            </MDBBtn>
            */}
          </div>

          <DragDropContext onDragEnd={onDragEnd}>
            <Droppable droppableId="lecture-subjects">
              {(provided) => (
                <div {...provided.droppableProps} ref={provided.innerRef}>
                  {subjects.map((subject, index) => (
                    <Draggable key={subject.id} draggableId={subject.id} index={index}>
                      {(provided, snapshot) => (
                        <MDBCard 
                          className={`mb-3 ${snapshot.isDragging ? 'shadow-lg' : ''}`}
                          ref={provided.innerRef}
                          {...provided.draggableProps}
                        >
                          <MDBCardBody>
                            <div className="d-flex align-items-start">
                              <div 
                                {...provided.dragHandleProps}
                                className="me-3 mt-2 text-muted"
                                style={{ cursor: 'grab' }}
                              >
                                <MDBIcon icon="grip-vertical" />
                              </div>
                              
                              <div className="flex-grow-1">
                                <div className="d-flex align-items-center mb-2">
                                  <MDBBadge color="secondary" className="me-2">
                                    #{index + 1}
                                  </MDBBadge>
                                  <MDBTypography tag="h6" className="mb-0">
                                    {t('Subject')}
                                  </MDBTypography>
                                </div>
                                
                                {/* Two-column layout for German and English */}
                                <MDBRow>
                                  <MDBCol md="6">
                                    <div className="d-flex align-items-center">
                                      <ReactCountryFlag 
                                        countryCode="DE" 
                                        svg 
                                        style={{ width: '1.2em', height: '1.2em', marginRight: '0.5rem' }}
                                      />
                                      <div className="flex-grow-1">
                                        <MDBInput
                                          label={t('German Subject')}
                                          value={subject.de}
                                          onChange={(e) => updateSubject(index, 'de', e.target.value)}
                                          required
                                          size="sm"
                                          maxLength={MAX_CHARACTERS}
                                          counter
                                        />
                                        {subject.de.length > 0 && (
                                          <div className="form-text d-flex justify-content-between">
                                            <span>
                                              {getRemainingChars(subject.de) > 0 && (
                                                <span className="text-warning">
                                                  {t('{{count}} more characters needed', { count: getRemainingChars(subject.de) })}
                                                </span>
                                              )}
                                              {getExcessChars(subject.de) > 0 && (
                                                <span className="text-danger">
                                                  {t('{{count}} characters too many', { count: getExcessChars(subject.de) })}
                                                </span>
                                              )}
                                              {isTextValid(subject.de) && (
                                                <span className="text-success">
                                                  ✓
                                                </span>
                                              )}
                                            </span>
                                            <span className="text-muted">
                                              {getCharacterCount(subject.de)}/{MAX_CHARACTERS}
                                            </span>
                                          </div>
                                        )}
                                      </div>
                                    </div>
                                  </MDBCol>
                                  
                                  <MDBCol md="6">
                                    <div className="d-flex align-items-center">
                                      <ReactCountryFlag 
                                        countryCode="US" 
                                        svg 
                                        style={{ width: '1.2em', height: '1.2em', marginRight: '0.5rem' }}
                                      />
                                      <div className="flex-grow-1">
                                        <MDBInput
                                          label={t('English Subject')}
                                          value={subject.en}
                                          onChange={(e) => updateSubject(index, 'en', e.target.value)}
                                          required
                                          size="sm"
                                          maxLength={MAX_CHARACTERS}
                                          counter
                                        />
                                        {subject.en.length > 0 && (
                                          <div className="form-text d-flex justify-content-between">
                                            <span>
                                              {getRemainingChars(subject.en) > 0 && (
                                                <span className="text-warning">
                                                  {t('{{count}} more characters needed', { count: getRemainingChars(subject.en) })}
                                                </span>
                                              )}
                                              {getExcessChars(subject.en) > 0 && (
                                                <span className="text-danger">
                                                  {t('{{count}} characters too many', { count: getExcessChars(subject.en) })}
                                                </span>
                                              )}
                                              {isTextValid(subject.en) && (
                                                <span className="text-success">
                                                  ✓
                                                </span>
                                              )}
                                            </span>
                                            <span className="text-muted">
                                              {getCharacterCount(subject.en)}/{MAX_CHARACTERS}
                                            </span>
                                          </div>
                                        )}
                                      </div>
                                    </div>
                                  </MDBCol>
                                </MDBRow>
                              </div>
                              
                              {/* Temporarily commented out Remove Subject button
                              <MDBBtn
                                size="sm"
                                color="danger"
                                outline
                                onClick={() => removeSubject(index)}
                                disabled={subjects.length <= SUBJECTS_SETTINGS.minSubjects}
                                className="ms-2"
                              >
                                <MDBIcon icon="trash" />
                              </MDBBtn>
                              */}
                            </div>
                          </MDBCardBody>
                        </MDBCard>
                      )}
                    </Draggable>
                  ))}
                  {provided.placeholder}
                </div>
              )}
            </Droppable>
          </DragDropContext>
        </MDBCol>
      </MDBRow>

      {hasFieldError('subjects') && (
        <MDBRow className="mb-3">
          <MDBCol>
            <MDBAlert color="danger">
              {getFieldError('subjects')}
            </MDBAlert>
          </MDBCol>
        </MDBRow>
      )}

      {!isValidSubjects() && (
        <MDBRow className="mb-3">
          <MDBCol>
            <MDBAlert color="warning">
              {(() => {
                const filledSubjects = subjects.filter(subject => 
                  isTextValid(subject.de.trim()) && isTextValid(subject.en.trim())
                );
                const hasPartiallyFilledSubjects = subjects.some(subject => {
                  const hasGerman = subject.de.trim().length > 0;
                  const hasEnglish = subject.en.trim().length > 0;
                  return (hasGerman && !hasEnglish) || (!hasGerman && hasEnglish);
                });
                const hasInvalidLengthSubjects = subjects.some(subject => {
                  const hasGerman = subject.de.trim().length > 0;
                  const hasEnglish = subject.en.trim().length > 0;
                  if (hasGerman && !isTextValid(subject.de.trim())) return true;
                  if (hasEnglish && !isTextValid(subject.en.trim())) return true;
                  return false;
                });
                
                if (filledSubjects.length < SUBJECTS_SETTINGS.minSubjects) {
                  return t('Please fill in at least {{min}} complete subjects ({{minChars}}-{{maxChars}} characters each, both German and English)', { 
                    min: SUBJECTS_SETTINGS.minSubjects, 
                    minChars: MIN_CHARACTERS, 
                    maxChars: MAX_CHARACTERS 
                  });
                }
                if (hasPartiallyFilledSubjects) {
                  return t('Please complete all partially filled subjects (both German and English required)');
                }
                if (hasInvalidLengthSubjects) {
                  return t('Please ensure all subjects have the correct length ({{minChars}}-{{maxChars}} characters)', { 
                    minChars: MIN_CHARACTERS, 
                    maxChars: MAX_CHARACTERS 
                  });
                }
                return t('Please check your subject entries');
              })()}
            </MDBAlert>
          </MDBCol>
        </MDBRow>
      )}

      {/* Validation Debug Info */}
      {isDevMode && (
        <MDBRow className="mb-4">
          <MDBCol>
            <MDBCard className="border-info">
              <MDBCardBody>
                <MDBTypography tag="h6" className="mb-3">
                  🔍 Validation Debug Info (Dev Mode) - Subjects Step
                </MDBTypography>
              
              <div className="mb-2">
                <strong>Step Required Fields:</strong> {requiredFields.join(', ') || 'none'}
              </div>
              
              <div className="mb-2">
                <strong>Subjects:</strong> {subjects.length} / {SUBJECTS_SETTINGS.maxSubjects}
              </div>
              
              
              <div className="mb-2">
                <strong>Local Validation:</strong>{' '}
                <MDBBadge color={isValidSubjects() ? 'success' : 'warning'}>
                  {isValidSubjects() ? 'Valid subjects structure' : 'Invalid subjects structure'}
                </MDBBadge>
              </div>
              
              <div className="mb-2">
                <strong>Schema Validation:</strong>{' '}
                <MDBBadge color={stepValidation.isValid ? 'success' : 'danger'}>
                  {stepValidation.isValid ? 'Valid' : 'Invalid'}
                </MDBBadge>
              </div>
              
              {stepValidation.errors.length > 0 && (
                <div className="mb-2">
                  <strong>Schema Errors:</strong>
                  <ul className="mb-0">
                    {stepValidation.errors.map((error, index) => (
                      <li key={index}>
                        <code>{error.field}</code>: {error.message}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              
              {subjectsFieldValidation && (
                <div className="mb-2">
                  <strong>Field-Specific Error:</strong>{' '}
                  <code>{subjectsFieldValidation.field}</code>: {subjectsFieldValidation.message}
                </div>
              )}
              
              <div className="mb-2">
                <strong>Continue Button:</strong>{' '}
                <MDBBadge color={canContinue ? 'success' : 'warning'}>
                  {canContinue ? 'Enabled' : 'Disabled'}
                </MDBBadge>
              </div>
              
              <div className="mb-2">
                <strong>Subjects Data (with temp IDs):</strong>
                <pre style={{ fontSize: '10px', maxHeight: '200px', overflow: 'auto' }}>
                  {JSON.stringify(subjects, null, 2)}
                </pre>
              </div>
              <div className="mb-2">
                <strong>Saved Data (without IDs):</strong>
                <pre style={{ fontSize: '10px', maxHeight: '200px', overflow: 'auto' }}>
                  {JSON.stringify(subjects.map(({ de, en }) => ({ de, en })), null, 2)}
                </pre>
              </div>
            </MDBCardBody>
          </MDBCard>
        </MDBCol>
      </MDBRow>
      )}

      </MDBContainer>
      
      {/* Fixed button container at bottom */}
      <div className="bg-white" style={{ 
        position: 'sticky', 
        bottom: 0,
        padding: '1rem',
        marginTop: 'auto',
        zIndex: 10
      }}>
        <MDBContainer>
          <MDBRow>
            <MDBCol className="d-flex justify-content-center gap-5">
              <MDBBtn 
                color="secondary" 
                onClick={goToPreviousStep}
                outline
              >
                {t('Back')}
              </MDBBtn>
              <MDBBtn 
                onClick={handleContinue}
                disabled={!canContinue}
              >
                {t('Continue')}
              </MDBBtn>
            </MDBCol>
          </MDBRow>
        </MDBContainer>
      </div>
    </div>
  );
}